import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CreateTimeEntryInput, PaginatedResult, TimeEntry } from '@catalyst-psa/shared';
import { RATE_SOURCE_EXPLANATIONS, RATE_SOURCE_LABELS, TIME_ENTRY_METHOD_LABELS } from '@catalyst-psa/shared';
import { useQuery } from '@tanstack/react-query';

import { ApiError, api } from '../../api/client.js';
import { useAuth } from '../../auth/authContext.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { SelectField, TextAreaField, TextField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { HistoryPanel } from '../audit/HistoryPanel.js';
import { buildQuery } from '../../api/queryString.js';
import { useProjectOptions, useProjectPhases } from '../projects/api.js';
import { ticketRefLabel } from '../tickets/reference.js';
import { TimeCustomerPicker } from './TimeCustomerPicker.js';
import {
  useBillingClassOptions,
  useCreateTimeEntry,
  useDeleteTimeEntry,
  useMemberOptions,
  useServiceCategoryOptions,
  useTimeEntryAgreementOptions,
  useUpdateTimeEntry,
  useUpdateTimeEntryBilling,
} from './api.js';

interface TimeEntryFormModalProps {
  isOpen: boolean;
  entry: TimeEntry | null;
  /** When creating, pre-select this member (e.g. the current user). */
  initialMemberId?: string;
  /** When creating, pre-fill the work date (yyyy-mm-dd). */
  initialWorkDate?: string;
  /** When creating, pre-fill the work item (e.g. from a timesheet grid cell). */
  initialWorkItem?: WorkItemDefaults;
  onClose: () => void;
}

/** Work-item context used to pre-fill a new time entry (e.g. a timesheet grid row). */
export interface WorkItemDefaults {
  projectId?: string | null;
  agreementId?: string | null;
  phaseId?: string | null;
  ticketId?: string | null;
  serviceCategoryId?: string | null;
  billingClassId?: string | null;
}

type Attachment = 'project' | 'agreement';

/** Pill-style navigation link (ctrl/cmd-click opens in a new tab). */
const NAV_CHIP_CLASS =
  'inline-flex items-center rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

interface FormState {
  attachment: Attachment | '';
  /** Guided create flow only: filters the project/agreement pickers. */
  customerId: string;
  projectId: string;
  phaseId: string;
  ticketId: string;
  agreementId: string;
  serviceCategoryId: string;
  billingClassId: string;
  memberId: string;
  workDate: string;
  hours: string;
  notes: string;
  internalNotes: string;
  cuTaskId: string;
  cuTimeId: string;
  cuSubTaskId: string;
  cuSubTaskTitle: string;
  rateOverride: string;
}

function emptyForm(
  initialMemberId?: string,
  initialWorkDate?: string,
  initialWorkItem?: WorkItemDefaults,
): FormState {
  return {
    attachment: initialWorkItem?.agreementId
      ? 'agreement'
      : initialWorkItem?.projectId
        ? 'project'
        : '',
    customerId: '',
    projectId: initialWorkItem?.projectId ?? '',
    phaseId: initialWorkItem?.phaseId ?? '',
    ticketId: initialWorkItem?.ticketId ?? '',
    agreementId: initialWorkItem?.agreementId ?? '',
    serviceCategoryId: initialWorkItem?.serviceCategoryId ?? '',
    billingClassId: initialWorkItem?.billingClassId ?? '',
    memberId: initialMemberId ?? '',
    workDate: initialWorkDate ?? new Date().toISOString().slice(0, 10),
    hours: '',
    notes: '',
    internalNotes: '',
    cuTaskId: '',
    cuTimeId: '',
    cuSubTaskId: '',
    cuSubTaskTitle: '',
    rateOverride: '',
  };
}

function toFormState(
  entry: TimeEntry | null,
  initialMemberId?: string,
  initialWorkDate?: string,
  initialWorkItem?: WorkItemDefaults,
): FormState {
  if (!entry) {
    return emptyForm(initialMemberId, initialWorkDate, initialWorkItem);
  }
  return {
    attachment: entry.agreementId ? 'agreement' : 'project',
    customerId: entry.customerId ?? '',
    projectId: entry.projectId ?? '',
    phaseId: entry.phaseId ?? '',
    ticketId: entry.ticketId ?? '',
    agreementId: entry.agreementId ?? '',
    serviceCategoryId: entry.serviceCategoryId,
    billingClassId: entry.billingClassId,
    memberId: entry.memberId,
    workDate: entry.workDate,
    hours: entry.hours,
    notes: entry.notes ?? '',
    internalNotes: entry.internalNotes ?? '',
    cuTaskId: entry.cuTaskId ?? '',
    cuTimeId: entry.cuTimeId ?? '',
    cuSubTaskId: entry.cuSubTaskId ?? '',
    cuSubTaskTitle: entry.cuSubTaskTitle ?? '',
    rateOverride: entry.rateOverride ?? '',
  };
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

/** Editable billing-status fields (managed via the admin billing-audit endpoint). */
interface BillingState {
  billedExternally: boolean;
  externalInvoiceRef: string;
  externalInvoiceDate: string;
  writtenOff: boolean;
  rateOverride: string;
}

function toBillingState(entry: TimeEntry | null): BillingState {
  return {
    billedExternally: entry?.billedExternally ?? false,
    externalInvoiceRef: entry?.externalInvoiceRef ?? '',
    externalInvoiceDate: entry?.externalInvoiceDate ? entry.externalInvoiceDate.slice(0, 10) : '',
    writtenOff: entry?.writtenOff ?? false,
    rateOverride: entry?.rateOverride ?? '',
  };
}

/** Formats an ISO timestamp as a short local date, or an em dash when absent. */
function formatDateOrDash(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/** A shaded, titled grouping of form fields (Required / Optional / System). */
function FormSection({
  title,
  tone,
  className,
  children,
}: {
  title: string;
  tone: 'required' | 'optional' | 'system';
  className?: string;
  children: ReactNode;
}): JSX.Element {
  // In dark mode the slate palette is remapped (low = dark, high = light), so the
  // Optional/System panels render LIGHT. Their field labels (text-slate-700 → light in
  // dark mode) would be unreadable, so force the labels + heading dark with explicit hex
  // (arbitrary values bypass the palette remap). The Required panel stays dark (brand),
  // so its light labels remain readable and need no override.
  const darkLabels = 'dark:[&_label]:text-[#0f172a] dark:[&_h3]:text-[#334155]';
  const toneClass =
    tone === 'required'
      ? 'border-brand-200 bg-brand-50 dark:border-brand-800 dark:bg-brand-900/20'
      : tone === 'optional'
        ? `border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/40 ${darkLabels}`
        : `border-slate-300 bg-slate-100 dark:border-slate-700 dark:bg-slate-800/70 ${darkLabels}`;
  return (
    <div className={`rounded-lg border p-4 ${toneClass} ${className ?? ''}`}>
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {title}
      </h3>
      <div className="flex flex-col gap-4">{children}</div>
    </div>
  );
}

export function TimeEntryFormModal({
  isOpen,
  entry: entryProp,
  initialMemberId,
  initialWorkDate,
  initialWorkItem,
  onClose,
}: TimeEntryFormModalProps): JSX.Element {
  const navigate = useNavigate();
  const [entry, setEntry] = useState<TimeEntry | null>(entryProp);
  const isEdit = entry !== null;
  const { can, isAdmin } = useAuth();
  const canDelete = can('time', 'delete');
  const saveMode = useSaveMode();
  const resetSaveMode = saveMode.reset;

  const [form, setForm] = useState<FormState>(() =>
    toFormState(entry, initialMemberId, initialWorkDate, initialWorkItem),
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [tab, setTab] = useState<'details' | 'history'>('details');
  // Guided-flow customer selection (create mode): display name + the picked customer's
  // default support agreement, captured from the time-gated picker option.
  const [customerName, setCustomerName] = useState('');
  const [customerDefaultAgreementId, setCustomerDefaultAgreementId] = useState<string | null>(
    null,
  );

  // Billing-status edits go through the admin billing-audit endpoint (not the labor
  // update), so they aren't blocked by an approved timesheet and correctly detach a
  // written-off entry from its invoice. `billingBaseline` tracks the last-saved values.
  const [billing, setBilling] = useState<BillingState>(() => toBillingState(entry));
  const [billingBaseline, setBillingBaseline] = useState<BillingState>(() => toBillingState(entry));
  const billingMutation = useUpdateTimeEntryBilling();
  const resetBilling = billingMutation.reset;

  const createMutation = useCreateTimeEntry();
  const updateMutation = useUpdateTimeEntry();
  const deleteMutation = useDeleteTimeEntry();
  const mutation = isEdit ? updateMutation : createMutation;
  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;

  const membersQuery = useMemberOptions();
  const categoriesQuery = useServiceCategoryOptions();
  const billingClassesQuery = useBillingClassOptions();
  const projectsQuery = useProjectOptions(isOpen);
  // Agreement picks go through the TIME-gated options endpoint (technicians usually
  // lack the agreements RBAC area). Creating scopes to the picked customer's active
  // agreements; a launcher-prefilled or edited entry's agreement is force-included.
  const prefilledAgreementId = initialWorkItem?.agreementId ?? null;
  const agreementsQuery = useTimeEntryAgreementOptions(
    isEdit ? (entry.customerId ?? '') : form.customerId,
    isEdit ? (entry.agreementId ?? '') : (prefilledAgreementId ?? ''),
    isOpen,
  );
  const phasesQuery = useProjectPhases(form.attachment === 'project' ? form.projectId : '');
  // Project entries pick from the project's tickets (narrowed to the picked phase when
  // creating); agreement entries pick from the agreement's open support tickets (closed
  // ones would swamp a pay-as-you-go agreement).
  const ticketPhaseFilter = !isEdit && form.attachment === 'project' ? form.phaseId : '';
  const ticketScope =
    form.attachment === 'project'
      ? { projectId: form.projectId, ...(ticketPhaseFilter ? { phaseId: ticketPhaseFilter } : {}) }
      : { agreementId: form.agreementId, open: true };
  const ticketScopeId = form.attachment === 'project' ? form.projectId : form.agreementId;
  const ticketsQuery = useQuery({
    queryKey: ['tickets', 'options', form.attachment, ticketScopeId, ticketPhaseFilter] as const,
    queryFn: () =>
      api.get<
        PaginatedResult<{
          id: string;
          number: number;
          referenceId: string | null;
          title: string;
          defaultServiceCategoryId: string | null;
          defaultBillingClassId: string | null;
        }>
      >(`/tickets${buildQuery({ page: 1, pageSize: 200, ...ticketScope })}`),
    enabled: isOpen && Boolean(ticketScopeId),
  });

  const memberOptions = useMemo(
    () => (membersQuery.data ?? []).map((m) => ({ value: m.id, label: m.displayName })),
    [membersQuery.data],
  );
  const categoryOptions = useMemo(
    () => (categoriesQuery.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    [categoriesQuery.data],
  );
  const billingClassOptions = useMemo(
    () => (billingClassesQuery.data ?? []).map((b) => ({ value: b.id, label: b.name })),
    [billingClassesQuery.data],
  );
  const projectOptions = useMemo(() => {
    // Creating narrows the list to the picked customer's ACTIVE projects (none until a
    // customer is chosen); editing keeps the full catalog for display.
    const source = isEdit
      ? (projectsQuery.data ?? [])
      : (projectsQuery.data ?? []).filter(
          (p) => p.customerId === form.customerId && p.status === 'active',
        );
    const opts = source.map((p) => ({
      value: p.id,
      label: `#${p.number} — ${p.name} (${p.customerName})`,
    }));
    // Safety net: if the linked project somehow isn't in the options list, still show it
    // so the native <select> matches the entry's real projectId.
    if (entry?.projectId && !opts.some((o) => o.value === entry.projectId)) {
      opts.unshift({ value: entry.projectId, label: entry.workLabel ?? 'Current project' });
    }
    // A prefilled launcher (timesheet grid cell) can carry a non-active project — keep
    // it selectable so the prefill still displays.
    if (!isEdit && form.projectId && !opts.some((o) => o.value === form.projectId)) {
      const p = (projectsQuery.data ?? []).find((row) => row.id === form.projectId);
      if (p) opts.unshift({ value: p.id, label: `#${p.number} — ${p.name} (${p.customerName})` });
    }
    return opts;
  }, [projectsQuery.data, entry, isEdit, form.customerId, form.projectId]);
  const agreementOptions = useMemo(
    () => (agreementsQuery.data ?? []).map((a) => ({ value: a.id, label: a.name })),
    [agreementsQuery.data],
  );
  const phaseOptions = useMemo(
    () => [
      { value: '', label: 'No phase' },
      ...(phasesQuery.data?.data ?? []).map((p) => ({ value: p.id, label: p.name })),
    ],
    [phasesQuery.data],
  );
  const ticketOptions = useMemo(() => {
    const opts = [
      { value: '', label: 'No ticket' },
      ...(ticketsQuery.data?.data ?? []).map((t) => ({
        value: t.id,
        label: `${ticketRefLabel(t.referenceId, t.number)} — ${t.title}`,
      })),
    ];
    // Safety net: a closed or unlisted ticket must still show so the native <select>
    // matches the entry's real ticketId instead of silently falling back to "No ticket".
    if (entry?.ticketId && !opts.some((o) => o.value === entry.ticketId)) {
      opts.splice(1, 0, {
        value: entry.ticketId,
        label: `${ticketRefLabel(entry.ticketReference, entry.ticketNumber)}${
          entry.ticketTitle ? ` — ${entry.ticketTitle}` : ''
        }`,
      });
    }
    return opts;
  }, [ticketsQuery.data, entry]);

  const selectedProjectCustomerId = useMemo(
    () =>
      (projectsQuery.data ?? []).find((project) => project.id === form.projectId)?.customerId ??
      // Fall back to the entry's own resolved customer when its project isn't loaded,
      // so customer-scoped fields still resolve correctly.
      (entry?.projectId === form.projectId ? (entry.customerId ?? null) : null),
    [projectsQuery.data, form.projectId, entry],
  );
  const selectedAgreement = useMemo(
    () => (agreementsQuery.data ?? []).find((agreement) => agreement.id === form.agreementId),
    [agreementsQuery.data, form.agreementId],
  );
  const selectedAgreementCustomerId = selectedAgreement?.customerId ?? null;
  const customerIdForGoTo =
    entry?.customerId ??
    (form.attachment === 'project' ? selectedProjectCustomerId : selectedAgreementCustomerId);

  // ---- Guided create flow: customer → logged against → work item → hours/notes. ----
  const phasesList = phasesQuery.data?.data ?? [];
  const ticketsList = ticketsQuery.data?.data ?? [];
  // A step counts as satisfied once its options are loaded and either a pick was made
  // or there is genuinely nothing to pick (a project with no phases/tickets).
  const phaseSatisfied =
    form.attachment !== 'project' ||
    (Boolean(form.projectId) &&
      phasesQuery.isSuccess &&
      (phasesList.length === 0 || Boolean(form.phaseId)));
  const ticketSatisfied =
    ticketsQuery.isSuccess && (ticketsList.length === 0 || Boolean(form.ticketId));
  const workItemComplete =
    isEdit ||
    (Boolean(form.customerId) &&
      (form.attachment === 'project'
        ? Boolean(form.projectId) && phaseSatisfied && ticketSatisfied
        : form.attachment === 'agreement'
          ? Boolean(form.agreementId) && ticketSatisfied
          : false));

  // Prefilled launchers (timesheet grid cell, ticket Add-hour) carry a work item but no
  // customer — back-fill it (and the display name) once the pick resolves.
  useEffect(() => {
    if (!isOpen || isEdit || form.customerId) return;
    if (form.attachment === 'agreement' && form.agreementId && selectedAgreement) {
      setCustomerName(selectedAgreement.customerName);
      setForm((f) => (f.customerId ? f : { ...f, customerId: selectedAgreement.customerId }));
      return;
    }
    if (form.attachment === 'project' && form.projectId && selectedProjectCustomerId) {
      const project = (projectsQuery.data ?? []).find((p) => p.id === form.projectId);
      if (project) setCustomerName(project.customerName);
      setForm((f) => (f.customerId ? f : { ...f, customerId: selectedProjectCustomerId }));
    }
  }, [
    isOpen,
    isEdit,
    form.customerId,
    form.attachment,
    form.projectId,
    form.agreementId,
    selectedProjectCustomerId,
    selectedAgreement,
    projectsQuery.data,
  ]);

  // Picking the agreement path pre-selects the customer's Default Support Agreement
  // (captured from the picked customer option; the user can still pick another).
  useEffect(() => {
    if (!isOpen || isEdit || form.attachment !== 'agreement' || form.agreementId) return;
    const defaultId = customerDefaultAgreementId;
    if (defaultId && agreementOptions.some((o) => o.value === defaultId)) {
      setForm((f) =>
        f.agreementId ? f : { ...f, agreementId: defaultId, ticketId: '' },
      );
    }
  }, [
    isOpen,
    isEdit,
    form.attachment,
    form.agreementId,
    customerDefaultAgreementId,
    agreementOptions,
  ]);

  // Auto-set the service category AND billing class from the picks (ticket default,
  // else phase override, else project default — the ticket's defaults are already
  // resolved server-side). Tracks the last derived value so a manual change afterwards
  // isn't clobbered — only a NEW pick re-derives it.
  const lastDerivedCategoryRef = useRef<string | null>(null);
  const lastDerivedBillingClassRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen || isEdit) return;
    const ticket = ticketsList.find((t) => t.id === form.ticketId);
    const phase =
      form.attachment === 'project' && form.projectId
        ? phasesList.find((p) => p.id === form.phaseId)
        : undefined;
    const project =
      form.attachment === 'project' && form.projectId
        ? (projectsQuery.data ?? []).find((p) => p.id === form.projectId)
        : undefined;
    const derived =
      ticket?.defaultServiceCategoryId ??
      phase?.overrideServiceCategoryId ??
      project?.defaultServiceCategoryId ??
      null;
    if (derived && derived !== lastDerivedCategoryRef.current) {
      lastDerivedCategoryRef.current = derived;
      setForm((f) => (f.serviceCategoryId === derived ? f : { ...f, serviceCategoryId: derived }));
    }
    const derivedBillingClass =
      ticket?.defaultBillingClassId ??
      phase?.overrideBillingClassId ??
      project?.defaultBillingClassId ??
      null;
    if (derivedBillingClass && derivedBillingClass !== lastDerivedBillingClassRef.current) {
      lastDerivedBillingClassRef.current = derivedBillingClass;
      setForm((f) =>
        f.billingClassId === derivedBillingClass
          ? f
          : { ...f, billingClassId: derivedBillingClass },
      );
    }
  }, [
    isOpen,
    isEdit,
    form.ticketId,
    form.phaseId,
    form.projectId,
    form.attachment,
    ticketsList,
    phasesList,
    projectsQuery.data,
  ]);

  const initialProjectId = initialWorkItem?.projectId ?? null;
  const initialAgreementId = initialWorkItem?.agreementId ?? null;
  const initialPhaseId = initialWorkItem?.phaseId ?? null;
  const initialTicketId = initialWorkItem?.ticketId ?? null;
  const initialServiceCategoryId = initialWorkItem?.serviceCategoryId ?? null;
  const initialBillingClassId = initialWorkItem?.billingClassId ?? null;

  useEffect(() => {
    if (isOpen) {
      setEntry(entryProp);
    }
  }, [isOpen, entryProp]);

  useEffect(() => {
    if (isOpen) {
      setForm(
        toFormState(entry, initialMemberId, initialWorkDate, {
          projectId: initialProjectId,
          agreementId: initialAgreementId,
          phaseId: initialPhaseId,
          ticketId: initialTicketId,
          serviceCategoryId: initialServiceCategoryId,
          billingClassId: initialBillingClassId,
        }),
      );
      setConfirmOpen(false);
      setTab('details');
      setBilling(toBillingState(entry));
      setBillingBaseline(toBillingState(entry));
      setCustomerName('');
      setCustomerDefaultAgreementId(null);
      lastDerivedCategoryRef.current = null;
      lastDerivedBillingClassRef.current = null;
      resetSaveMode();
      resetBilling();
      resetCreate();
      resetUpdate();
    }
  }, [
    isOpen,
    entry,
    initialMemberId,
    initialWorkDate,
    initialProjectId,
    initialAgreementId,
    initialPhaseId,
    initialTicketId,
    initialServiceCategoryId,
    initialBillingClassId,
    resetSaveMode,
    resetBilling,
    resetCreate,
    resetUpdate,
  ]);

  // Default the billing class to the configured default when creating.
  useEffect(() => {
    if (isOpen && !isEdit && !form.billingClassId && billingClassesQuery.data?.length) {
      const def = billingClassesQuery.data.find((b) => b.isDefault) ?? billingClassesQuery.data[0];
      if (def) {
        setForm((f) => (f.billingClassId ? f : { ...f, billingClassId: def.id }));
      }
    }
  }, [isOpen, isEdit, form.billingClassId, billingClassesQuery.data]);

  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError =
    mutation.error instanceof ApiError && !mutation.error.envelope?.error.details?.length
      ? mutation.error.message
      : mutation.isError && !(mutation.error instanceof ApiError)
        ? 'Failed to save time entry.'
        : null;

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (!isEdit && !workItemComplete) return;

    if (isEdit) {
      const input: Parameters<typeof updateMutation.mutate>[0]['input'] = {
        serviceCategoryId: form.serviceCategoryId,
        billingClassId: form.billingClassId,
        workDate: form.workDate,
        hours: form.hours.trim(),
        notes: form.notes.trim() ? form.notes.trim() : null,
        internalNotes: form.internalNotes.trim() ? form.internalNotes.trim() : null,
        cuTaskId: form.cuTaskId.trim() ? form.cuTaskId.trim() : null,
        cuTimeId: form.cuTimeId.trim() ? form.cuTimeId.trim() : null,
        cuSubTaskId: form.cuSubTaskId.trim() ? form.cuSubTaskId.trim() : null,
        cuSubTaskTitle: form.cuSubTaskTitle.trim() ? form.cuSubTaskTitle.trim() : null,
        rateOverride: form.rateOverride.trim() ? form.rateOverride.trim() : null,
      };
      if (form.attachment === 'project') {
        input.phaseId = form.phaseId || null;
      }
      input.ticketId = form.ticketId || null;
      updateMutation.mutate(
        { id: entry.id, input },
        { onSuccess: () => saveMode.closeAfterSaveRef.current && onClose() },
      );
      return;
    }

    const payload: CreateTimeEntryInput = {
      serviceCategoryId: form.serviceCategoryId,
      billingClassId: form.billingClassId,
      memberId: form.memberId,
      workDate: form.workDate,
      hours: form.hours.trim(),
      notes: form.notes.trim() ? form.notes.trim() : null,
      internalNotes: form.internalNotes.trim() ? form.internalNotes.trim() : null,
      cuTaskId: form.cuTaskId.trim() ? form.cuTaskId.trim() : null,
      cuTimeId: form.cuTimeId.trim() ? form.cuTimeId.trim() : null,
      cuSubTaskId: form.cuSubTaskId.trim() ? form.cuSubTaskId.trim() : null,
      cuSubTaskTitle: form.cuSubTaskTitle.trim() ? form.cuSubTaskTitle.trim() : null,
      rateOverride: form.rateOverride.trim() ? form.rateOverride.trim() : null,
      ticketId: form.ticketId || null,
      ...(form.attachment === 'project'
        ? {
            projectId: form.projectId || undefined,
            phaseId: form.phaseId || null,
          }
        : { agreementId: form.agreementId || undefined }),
    };
    createMutation.mutate(payload, {
      onSuccess: (created) => {
        if (saveMode.closeAfterSaveRef.current) {
          onClose();
        } else {
          setEntry(created);
        }
      },
    });
  };

  const canEditBilling = isAdmin;
  const billingDirty =
    billing.billedExternally !== billingBaseline.billedExternally ||
    billing.externalInvoiceRef.trim() !== billingBaseline.externalInvoiceRef.trim() ||
    billing.externalInvoiceDate !== billingBaseline.externalInvoiceDate ||
    billing.writtenOff !== billingBaseline.writtenOff ||
    billing.rateOverride.trim() !== billingBaseline.rateOverride.trim();

  const handleSaveBilling = (): void => {
    if (!entry || !canEditBilling) {
      return;
    }
    const next: BillingState = {
      billedExternally: billing.billedExternally,
      externalInvoiceRef: billing.externalInvoiceRef.trim(),
      externalInvoiceDate: billing.externalInvoiceDate,
      writtenOff: billing.writtenOff,
      rateOverride: billing.rateOverride.trim(),
    };
    billingMutation.mutate(
      {
        id: entry.id,
        input: {
          billedExternally: next.billedExternally,
          externalInvoiceRef: next.externalInvoiceRef ? next.externalInvoiceRef : null,
          externalInvoiceDate: next.externalInvoiceDate ? next.externalInvoiceDate : null,
          writtenOff: next.writtenOff,
          ...(next.rateOverride !== billingBaseline.rateOverride.trim()
            ? { rateOverride: next.rateOverride ? next.rateOverride : null }
            : {}),
        },
      },
      {
        onSuccess: (updated) => {
          setBillingBaseline(next);
          // Reflect the re-resolved rate without waiting for a refetch.
          setEntry((e) =>
            e
              ? {
                  ...e,
                  rateOverride: next.rateOverride ? next.rateOverride : null,
                  resolvedRate: updated.resolvedRate,
                }
              : e,
          );
        },
      },
    );
  };

  const handleDelete = (): void => {
    if (!entry) {
      return;
    }
    deleteMutation.mutate(entry.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        onClose();
      },
    });
  };

  const goToCustomer = (): void => {
    if (!customerIdForGoTo) {
      return;
    }
    navigate(`/customers?returnCustomerId=${customerIdForGoTo}&returnTab=time`);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? 'Edit time entry' : 'Log time'}
        size="lg"
        tabs={
          isEdit
            ? [
                { id: 'details', label: 'Details' },
                { id: 'history', label: 'History' },
              ]
            : undefined
        }
        activeTabId={tab}
        onTabChange={(id) => setTab(id as 'details' | 'history')}
        headerActions={
          <Button
            variant="secondary"
            size="sm"
            onClick={goToCustomer}
            disabled={!customerIdForGoTo}
          >
            Go to Customer
          </Button>
        }
        footer={
          <FormActions
            formId="time-entry-form"
            saveMode={saveMode}
            isPending={mutation.isPending}
            disabled={!isEdit && !workItemComplete}
            onCancel={onClose}
            leading={
              isEdit && canDelete ? (
                <Button
                  variant="ghost"
                  className="mr-auto text-rose-600 hover:bg-rose-50"
                  onClick={() => setConfirmOpen(true)}
                  disabled={mutation.isPending || deleteMutation.isPending}
                >
                  Delete
                </Button>
              ) : null
            }
          />
        }
      >
        <form
          id="time-entry-form"
          onSubmit={handleSubmit}
          className={`flex flex-col gap-4 ${isEdit && tab !== 'details' ? 'hidden' : ''}`}
        >
          {generalError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {generalError}
            </div>
          ) : null}

          {isEdit && entry ? (
            <div className="flex flex-wrap items-center gap-2">
              {entry.customerId ? (
                <a
                  href={`/customers?returnCustomerId=${entry.customerId}&returnTab=details`}
                  className={NAV_CHIP_CLASS}
                >
                  Customer{entry.customerName ? `: ${entry.customerName}` : ''} ↗
                </a>
              ) : null}
              {entry.projectId ? (
                <a href={`/projects/${entry.projectId}`} className={NAV_CHIP_CLASS}>
                  {entry.workLabel ?? 'Open project'} ↗
                </a>
              ) : null}
              {entry.agreementId ? (
                <a href={`/agreements/${entry.agreementId}`} className={NAV_CHIP_CLASS}>
                  {entry.workLabel ?? 'Open agreement'} ↗
                </a>
              ) : null}
              {entry.ticketId ? (
                <a href={`/tickets?open=${entry.ticketId}`} className={NAV_CHIP_CLASS}>
                  Ticket {ticketRefLabel(entry.ticketReference, entry.ticketNumber)} ↗
                </a>
              ) : null}
            </div>
          ) : null}

          <FormSection
            title="Required Information"
            tone="required"
            className={isEdit ? undefined : 'order-2'}
          >
            <div className="grid grid-cols-2 gap-4">
              <TextField
                label="Work date"
                type="date"
                required
                value={form.workDate}
                onChange={(e) => setForm((f) => ({ ...f, workDate: e.target.value }))}
                error={fieldErrors.workDate}
              />
              <TextField
                label="Hours"
                required
                inputMode="decimal"
                placeholder="1.50"
                disabled={!isEdit && !workItemComplete}
                value={form.hours}
                onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))}
                error={fieldErrors.hours}
              />
            </div>
            <TextAreaField
              label="Notes"
              required
              rows={3}
              disabled={!isEdit && !workItemComplete}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              error={fieldErrors.notes}
            />
            {!isEdit && !workItemComplete ? (
              <p className="text-xs text-slate-500">
                Pick the customer and work item above before entering hours and notes.
              </p>
            ) : null}
          </FormSection>

          <FormSection
            title="Optional Information"
            tone="optional"
            className={isEdit ? undefined : 'order-3'}
          >
            <SelectField
              label="Billing class"
              required
              value={form.billingClassId}
              placeholder={billingClassesQuery.isLoading ? 'Loading…' : 'Select a billing class'}
              options={billingClassOptions}
              onChange={(e) => setForm((f) => ({ ...f, billingClassId: e.target.value }))}
              error={fieldErrors.billingClassId}
            />
            <TextAreaField
              label="Internal notes"
              rows={3}
              value={form.internalNotes}
              onChange={(e) => setForm((f) => ({ ...f, internalNotes: e.target.value }))}
              error={fieldErrors.internalNotes}
            />
          </FormSection>

          <FormSection
            title={isEdit ? 'System Information' : 'Work Item'}
            tone="system"
            className={isEdit ? undefined : 'order-1'}
          >
            {!isEdit ? (
              <TimeCustomerPicker
                label="Customer"
                required
                value={form.customerId}
                displayName={customerName}
                placeholder="Search customers…"
                onChange={(option) => {
                  setCustomerName(option.name);
                  setCustomerDefaultAgreementId(option.defaultSupportAgreementId);
                  setForm((f) => ({
                    ...f,
                    customerId: option.id,
                    projectId: '',
                    phaseId: '',
                    ticketId: '',
                    agreementId: '',
                  }));
                }}
              />
            ) : null}
            <SelectField
              label="Logged against"
              required
              disabled={isEdit || !form.customerId}
              value={form.attachment}
              placeholder={!isEdit && !form.customerId ? 'Pick a customer first' : 'Select…'}
              options={[
                { value: 'project', label: 'Project' },
                { value: 'agreement', label: 'Agreement' },
              ]}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  attachment: e.target.value as Attachment,
                  projectId: '',
                  phaseId: '',
                  ticketId: '',
                  agreementId: '',
                }))
              }
            />

            {form.attachment === '' ? null : form.attachment === 'project' ? (
              <>
                <SelectField
                  label="Project"
                  required
                  disabled={isEdit}
                  value={form.projectId}
                  placeholder={
                    !isEdit && !form.customerId
                      ? 'Pick a customer first'
                      : projectsQuery.isLoading
                        ? 'Loading…'
                        : !isEdit && projectOptions.length === 0
                          ? 'No projects for this customer'
                          : 'Select a project'
                  }
                  options={projectOptions}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      projectId: e.target.value,
                      phaseId: '',
                      ticketId: '',
                    }))
                  }
                  error={fieldErrors.projectId}
                />
                <div className="grid grid-cols-2 gap-4">
                  <SelectField
                    label="Phase"
                    required={!isEdit && phasesList.length > 0}
                    value={form.phaseId}
                    disabled={!form.projectId}
                    placeholder={
                      isEdit
                        ? undefined
                        : !form.projectId
                          ? 'Pick a project first'
                          : phasesQuery.isLoading
                            ? 'Loading…'
                            : phasesList.length === 0
                              ? 'No phases on this project'
                              : 'Select a phase'
                    }
                    options={isEdit ? phaseOptions : phaseOptions.slice(1)}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, phaseId: e.target.value, ticketId: '' }))
                    }
                    error={fieldErrors.phaseId}
                  />
                  <SelectField
                    label="Ticket"
                    required={!isEdit && ticketsList.length > 0}
                    value={form.ticketId}
                    disabled={
                      !form.projectId || (!isEdit && phasesList.length > 0 && !form.phaseId)
                    }
                    placeholder={
                      isEdit
                        ? undefined
                        : !form.projectId
                          ? 'Pick a project first'
                          : phasesList.length > 0 && !form.phaseId
                            ? 'Pick a phase first'
                            : ticketsQuery.isLoading
                              ? 'Loading…'
                              : ticketsList.length === 0
                                ? 'No tickets to pick'
                                : 'Select a ticket'
                    }
                    options={isEdit ? ticketOptions : ticketOptions.slice(1)}
                    onChange={(e) => setForm((f) => ({ ...f, ticketId: e.target.value }))}
                    error={fieldErrors.ticketId}
                  />
                </div>
              </>
            ) : (
              <>
                <SelectField
                  label="Agreement"
                  required
                  disabled={isEdit}
                  value={form.agreementId}
                  placeholder={
                    !isEdit && !form.customerId
                      ? 'Pick a customer first'
                      : agreementsQuery.isLoading
                        ? 'Loading…'
                        : !isEdit && agreementOptions.length === 0
                          ? 'No agreements for this customer'
                          : 'Select an agreement'
                  }
                  options={agreementOptions}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, agreementId: e.target.value, ticketId: '' }))
                  }
                  error={fieldErrors.agreementId}
                />
                <SelectField
                  label="Ticket"
                  required={!isEdit && ticketsList.length > 0}
                  value={form.ticketId}
                  disabled={!form.agreementId}
                  placeholder={
                    isEdit
                      ? undefined
                      : !form.agreementId
                        ? 'Pick an agreement first'
                        : ticketsQuery.isLoading
                          ? 'Loading…'
                          : ticketsList.length === 0
                            ? 'No open tickets to pick'
                            : 'Select a ticket'
                  }
                  options={isEdit ? ticketOptions : ticketOptions.slice(1)}
                  onChange={(e) => setForm((f) => ({ ...f, ticketId: e.target.value }))}
                  error={fieldErrors.ticketId}
                />
              </>
            )}

            <div className="grid grid-cols-2 gap-4">
              <SelectField
                label="Member"
                required
                disabled={isEdit}
                value={form.memberId}
                placeholder={membersQuery.isLoading ? 'Loading…' : 'Select a member'}
                options={memberOptions}
                onChange={(e) => setForm((f) => ({ ...f, memberId: e.target.value }))}
                error={fieldErrors.memberId}
              />
              <SelectField
                label="Service category"
                required
                value={form.serviceCategoryId}
                placeholder={categoriesQuery.isLoading ? 'Loading…' : 'Select a category'}
                options={categoryOptions}
                onChange={(e) => setForm((f) => ({ ...f, serviceCategoryId: e.target.value }))}
                error={fieldErrors.serviceCategoryId}
              />
            </div>

            <TextField
              label="Rate override"
              inputMode="decimal"
              placeholder="Optional"
              value={form.rateOverride}
              onChange={(e) => setForm((f) => ({ ...f, rateOverride: e.target.value }))}
              error={fieldErrors.rateOverride}
            />

            {entry && (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800/40">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-slate-500 dark:text-slate-400">Resolved rate</span>
                  <span
                    className="cursor-help font-semibold text-slate-800 underline decoration-dotted underline-offset-2 dark:text-slate-100"
                    title={`${RATE_SOURCE_LABELS[entry.rateSource]} — ${RATE_SOURCE_EXPLANATIONS[entry.rateSource]}`}
                  >
                    {currency.format(Number(entry.resolvedRate))} / hr
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Source: {RATE_SOURCE_LABELS[entry.rateSource]}
                </p>
              </div>
            )}
          </FormSection>

          {isEdit && entry ? (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/40">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Billing status
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Invoice</div>
                  <div className="font-medium text-slate-800 dark:text-slate-100">
                    {entry.invoiceId ? (
                      <a
                        className="text-brand-700 underline decoration-dotted underline-offset-2 hover:text-brand-800"
                        href={`/invoices/${entry.invoiceId}`}
                      >
                        {entry.invoiceNumber ?? 'On an invoice'}
                      </a>
                    ) : (
                      'Not invoiced'
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Invoiced</div>
                  <div className="font-medium text-slate-800 dark:text-slate-100">
                    {formatDateOrDash(entry.invoicedAt)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Entered via</div>
                  <div className="font-medium text-slate-800 dark:text-slate-100">
                    {entry.entryMethod ? TIME_ENTRY_METHOD_LABELS[entry.entryMethod] : '—'}
                  </div>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-3">
                <TextField
                  label="Rate override (admin fix)"
                  type="number"
                  placeholder="Leave blank for resolved rate"
                  disabled={!canEditBilling}
                  value={billing.rateOverride}
                  onChange={(e) => setBilling((b) => ({ ...b, rateOverride: e.target.value }))}
                />
                <div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Resolved rate</div>
                  <div className="mt-2 font-medium text-slate-800 dark:text-slate-100">
                    {entry.resolvedRate}
                  </div>
                  <div className="text-xs text-slate-400">
                    Works even after invoicing. A draft invoice this entry is on is re-priced
                    automatically; a sent invoice keeps its lines.
                  </div>
                </div>
              </div>

              <label className="mt-3 flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  disabled={!canEditBilling}
                  checked={billing.billedExternally}
                  onChange={(e) =>
                    setBilling((b) => ({ ...b, billedExternally: e.target.checked }))
                  }
                />
                <span className="text-slate-700 dark:text-slate-200">
                  Billed externally
                  <span className="block text-xs text-slate-400">
                    Already invoiced outside PSA (e.g. in ConnectWise); excluded from PSA invoicing.
                  </span>
                </span>
              </label>

              {billing.billedExternally ? (
                <div className="mt-2 grid grid-cols-2 gap-3 pl-6">
                  <TextField
                    label="External invoice reference"
                    placeholder="Optional (e.g. CW invoice #)"
                    maxLength={128}
                    disabled={!canEditBilling}
                    value={billing.externalInvoiceRef}
                    onChange={(e) =>
                      setBilling((b) => ({ ...b, externalInvoiceRef: e.target.value }))
                    }
                  />
                  <TextField
                    label="External invoice date"
                    type="date"
                    disabled={!canEditBilling}
                    value={billing.externalInvoiceDate}
                    onChange={(e) =>
                      setBilling((b) => ({ ...b, externalInvoiceDate: e.target.value }))
                    }
                  />
                </div>
              ) : null}

              <label className="mt-3 flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  disabled={!canEditBilling}
                  checked={billing.writtenOff}
                  onChange={(e) => setBilling((b) => ({ ...b, writtenOff: e.target.checked }))}
                />
                <span className="text-slate-700 dark:text-slate-200">
                  Written off
                  {entry.writtenOffAt || entry.writtenOffByName
                    ? ` (${[
                        entry.writtenOffByName ? `by ${entry.writtenOffByName}` : null,
                        entry.writtenOffAt ? `on ${formatDateOrDash(entry.writtenOffAt)}` : null,
                      ]
                        .filter(Boolean)
                        .join(' ')})`
                    : ''}
                  <span className="block text-xs text-slate-400">
                    Excluded from all invoicing without changing the billable flag. Writing off
                    removes the entry from any draft invoice it is on.
                  </span>
                </span>
              </label>

              {canEditBilling ? (
                <div className="mt-3 flex items-center gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleSaveBilling}
                    isLoading={billingMutation.isPending}
                    disabled={!billingDirty}
                  >
                    Update billing status
                  </Button>
                  {billingMutation.isError ? (
                    <span className="text-xs text-rose-600">Failed to update billing status.</span>
                  ) : billingMutation.isSuccess && !billingDirty ? (
                    <span className="text-xs text-emerald-600">Saved.</span>
                  ) : null}
                </div>
              ) : (
                <p className="mt-2 text-xs text-slate-400">
                  Only administrators can change billing status.
                </p>
              )}
            </div>
          ) : null}
        </form>
        {isEdit && entry ? (
          <div className={tab === 'history' ? '' : 'hidden'}>
            <HistoryPanel entityType="TimeEntry" entityId={entry.id} enabled={tab === 'history'} />
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Delete time entry"
        message="This permanently removes the time entry. This cannot be undone."
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
